/**
 * Сброс пароля по почте (auth-and-roles): запрос письма и установка нового пароля по токену.
 * Режим выбирается наличием `?token=` в URL.
 */
import { useState, type FormEvent } from 'react'
import { useSearchParams } from 'react-router-dom'
import { authApi } from '@/lib/api'
import { AppLink } from '@/app/navigation'
import { AuthShell } from './auth-shell'
import { Field } from '@/design-system/components/primitives/field'
import { Input } from '@/design-system/components/primitives/input'
import { PasswordInput } from '@/design-system/components/primitives/password-input'
import { Button } from '@/design-system/components/primitives/button'
import { Alert } from '@/design-system/components/feedback/alert'
import { getErrorMessage } from '@/lib/errors'

function RequestForm() {
  const [email, setEmail] = useState('')
  const [done, setDone] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await authApi.requestPasswordReset(email)
      setDone(true)
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setSubmitting(false)
    }
  }

  if (done) {
    return (
      <Alert variant="info">
        Если такая почта зарегистрирована, мы отправили на неё письмо со ссылкой для сброса пароля.
      </Alert>
    )
  }

  return (
    <form onSubmit={onSubmit} noValidate>
      {error ? (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      ) : null}
      <Field required label="Электронная почта">
        {(props) => (
          <Input
            {...props}
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        )}
      </Field>
      <Button type="submit" className="mt-2 w-full justify-center" disabled={submitting}>
        {submitting ? 'Отправка…' : 'Отправить ссылку'}
      </Button>
    </form>
  )
}

function ConfirmForm({ token }: { token: string }) {
  const [password, setPassword] = useState('')
  const [done, setDone] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await authApi.confirmPasswordReset(token, password)
      setDone(true)
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось сбросить пароль'))
    } finally {
      setSubmitting(false)
    }
  }

  if (done) {
    return (
      <Alert variant="info">
        Пароль изменён. Теперь можно <AppLink to="/login">войти</AppLink> с новым паролем.
      </Alert>
    )
  }

  return (
    <form onSubmit={onSubmit} noValidate>
      {error ? (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      ) : null}
      <Field required label="Новый пароль">
        {(props) => (
          <PasswordInput
            {...props}
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        )}
      </Field>
      <Button type="submit" className="mt-2 w-full justify-center" disabled={submitting}>
        {submitting ? 'Сохранение…' : 'Сохранить пароль'}
      </Button>
    </form>
  )
}

export function ResetPage() {
  const [params] = useSearchParams()
  const token = params.get('token')

  return (
    <AuthShell
      subtitle={token ? 'Установка нового пароля' : 'Восстановление доступа'}
      footer={
        <span className="text-txt-secondary">
          Вспомнили пароль? <AppLink to="/login">Войти</AppLink>
        </span>
      }
    >
      {token ? <ConfirmForm token={token} /> : <RequestForm />}
    </AuthShell>
  )
}
